/**
 * The shared, REAL entity-context helper every C1-C11 child resolver
 * consumes (C1, #22323). Resolves `{firm_id, entity_id}` from the already-
 * fetched `auth.oauthGrant` claim (`auth_context.tsx`'s `OAuthGrant`) — never
 * `firm.list`/`firm.create`/`entity.list`, none of which a WagePro OAuth
 * token can reach (a real customer's token is already bound to exactly one
 * entity; there is nothing to "list" or "create").
 *
 * This was the real-path counterpart to `dev_entity_context.ts`'s
 * dev-bypass-only, cookie-session-based `resolveDevEntityContext` -- that
 * module and every `dev_*.ts` resolver it served are deleted as of C12
 * (#22334), so this is now the ONLY entity-context resolver in the app.
 * Every child (C2 `use_employees`, C3 `use_pay_runs`, C4 `use_dashboard`,
 * C11 `use_settings`, …) calls `useOAuthEntityContext()` instead of
 * re-deriving its own entity context, so there is exactly one place in the
 * app that decides "does this session have a usable entity".
 *
 * `grant` is `null` here only in the (now-eliminated, see `auth_context.tsx`)
 * window between an `authenticated` boot and the grant actually resolving --
 * `resolveAuthBoot` never boots `authenticated` with a null grant (it either
 * retries once or surfaces an explicit `error` boot state instead). The type
 * still allows `null`, so this treats it identically to a null `entityId`:
 * no usable entity context, never a throw.
 */
import * as React from 'react';
import { useAuth, type OAuthGrant } from './auth_context.js';

export type OAuthEntityContext =
  | { status: 'ready'; firm_id: string; entity_id: string }
  | { status: 'no_entity' };

/**
 * Pure derivation, exported separately from the hook below so it is
 * unit-testable without rendering React (mirrors `resolveAuthBoot` /
 * `resolveMyPayState`'s "pure decision function + thin hook" split).
 */
export function resolveOAuthEntityContext(
  grant: OAuthGrant | null,
): OAuthEntityContext {
  if (!grant || grant.firmId === null || grant.entityId === null) {
    return { status: 'no_entity' };
  }
  return { status: 'ready', firm_id: grant.firmId, entity_id: grant.entityId };
}

/** This session's real entity context, derived from `useAuth()`'s `grant`. */
export function useOAuthEntityContext(): OAuthEntityContext {
  const { grant } = useAuth();
  return React.useMemo(() => resolveOAuthEntityContext(grant), [grant]);
}
